import React, { useContext, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import { LoadingButton } from "@mui/lab";
import { styled } from "@mui/material/styles";

import AttachFileIcon from "@mui/icons-material/AttachFile";

import FileOutlined from "../../assets/FileOutlined.svg";
import TrashOutlined from "../../assets/TrashIconOutlined.svg";

import { RootContext } from "../../context/RootContext";
import { LoansContext } from "../../context/LoansContext";
import { SUPPORTED_DOCUMENT_ACCEPT, partitionBySupportedFileType } from "../../utils/fileValidation";

import MitigationFactorSelect from "./MitigationFactorSelect";
import {
  MIN_MITIGATION_FACTORS,
  asMitigationFactorArray,
  fetchAndSyncWaivers,
  persistNewWaiver,
  uploadWaiverDocuments,
  WaiverServiceRecord,
} from "./utils";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export default function WaiverRequestForm({
  currentTabKey,
  testTitle,
  testType,
  testKey,
  partnerId,
  onSubmit,
  onCancel,
}: {
  currentTabKey?: string;
  testTitle?: string;
  testType?: string;
  testKey?: string;
  // The partner this specific test failed for (DSCR loans evaluate every
  // eligible partner independently — see BasicCustomCard.tsx). Falls back
  // to the loan's overall assignedPartner (from context) when not
  // provided, e.g. for Bridge loans, which don't evaluate per-partner.
  partnerId?: string;
  onSubmit?: (data: WaiverServiceRecord) => void;
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  const { loanDetails, setLoanDetails, assignedPartner, setWaiverDecisionVersion } = useContext(LoansContext) ?? {};
  const { decodedTokenData } = useContext(RootContext) ?? {};
  const { loanId } = useParams<{ loanId?: string }>();
  // Same responseBy-style resolution used elsewhere for the current user
  // (e.g. resetWaiverStatus/WaiverActionForm) — a human-readable display
  // string, not an id, since useUserDisplayName echoes back whatever it's
  // given when resolution fails, and the /online-sizer/users lookup
  // regularly fails to resolve real Keycloak ids anyway.
  const currentUserFallbackName =
    decodedTokenData?.name || decodedTokenData?.email || decodedTokenData?.user_id || "unknown";

  const [mitigationFactor, setMitigationFactor] = useState<string[]>([]);
  const [description, setDescription] = useState("");
  const isOtherSelected = mitigationFactor.includes("Other");
  // Files are only staged locally until Submit — the waiver-service upload
  // endpoints are scoped to a waiverId, which doesn't exist until the waiver
  // itself has been created, so nothing is uploaded anywhere yet at this
  // point (removing a staged file is a pure local no-op).
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [fileError, setFileError] = useState("");

  // accept only filters the native file-picker dialog — drag-and-drop
  // bypasses it entirely, so unsupported files are also rejected here.
  const addFiles = (fileList: FileList | File[]) => {
    const files = Array.from(fileList);
    if (!files.length) return;
    const { supported, unsupported } = partitionBySupportedFileType(files);
    setFileError(unsupported.length ? t("unsupportedFileType") : "");
    if (supported.length) setPendingFiles((prev) => [...prev, ...supported]);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(event.target.files ?? []);
    event.target.value = "";
  };

  const handleDragOver = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    setIsDragActive(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    setIsDragActive(false);
    if (event.dataTransfer.files) addFiles(event.dataTransfer.files);
  };

  const handleDeleteDocument = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, docIdx) => docIdx !== index));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError("");
    try {
      const record = await persistNewWaiver(
        loanId,
        setLoanDetails,
        {
          testTitle,
          testType,
          testKey,
          description,
          mitigationFactor: asMitigationFactorArray(mitigationFactor),
        },
        partnerId ?? assignedPartner,
        currentUserFallbackName,
      );
      const finalRecord = await uploadWaiverDocuments(setLoanDetails, record, pendingFiles);
      // Re-fetches the whole list from the waiver service and recomputes the
      // waiverReviewStatus flag from it (see fetchAndSyncWaivers/
      // syncWaiverReviewStatusFlag) — same post-action pattern
      // WaiverActionForm.tsx already uses for decisions, so a freshly
      // requested waiver is reflected on the list page right away instead of
      // waiting for an unrelated save to trigger the next sync.
      // syncReviewStatusFlag=true: this is a real waiver action (create), one
      // of the only cases that should re-patch waiverReviewStatus.
      await fetchAndSyncWaivers(loanId, setLoanDetails, setWaiverDecisionVersion, true);
      onSubmit?.(finalRecord);
    } catch (err) {
      setSubmitError(t("waiverSubmitFailed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      data-testid="waiver-request-form"
      sx={{
        marginTop: "24px",
        borderRadius: "6px",
        border: "1px solid #B8B8C7",
        background: "#fff",
      }}
    >
      <Grid container>
        <Grid
          item
          xs={12}
          style={{
            display: "flex",
            padding: "10px 16px",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "16px",
            alignSelf: "stretch",
            borderRadius: "4px 4px 0 0",
            border: "1px solid #DBE2FF",
            background: "#F8F9FD",
            width: "auto"
          }}
        >
          <h3
            style={{
              color: "#000",
              fontFamily: "Poppins",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "normal",
              margin: 0,
              padding: 0,
            }}
          >
            {t("requestingWaiver")}
          </h3>
        </Grid>
      </Grid>
      <Grid container style={{ padding: "16px",}}>
        <Grid item xs={12}>
          <FormControl fullWidth size="small">
            <label
              style={{
                fontSize: "12px",
                lineHeight: "20px",
                letterSpacing: "0.4px",
                fontWeight: 300,
              }}
              htmlFor="mitigation-factor-label"
            >
              {t("compensatingFactor")} <sup style={{ color: "red" }}>*</sup>
            </label>
            <MitigationFactorSelect
              value={mitigationFactor}
              onChange={(selected) => {
                setMitigationFactor(selected);
                if (!selected.includes("Other")) {
                  setDescription("");
                }
              }}
              labelId="mitigation-factor-label"
              testId="mitigation-factor-select"
            />
          </FormControl>
        </Grid>

        {isOtherSelected ? (
          <Grid item xs={12} style={{ paddingTop: "16px" }}>
            <span
              style={{
                fontSize: "12px",
                lineHeight: "20px",
                letterSpacing: "0.4px",
                fontWeight: 300,
              }}
            >
              {t("descriptionForOther")}
              <sup style={{ color: "red" }}>*</sup>
            </span>
            <TextField
              fullWidth
              multiline
              minRows={3}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              data-testid="waiver-description-input"
              style={{ background: "#F8F9FD",}}
            />
          </Grid>
        ) : null}

        <Grid item xs={12} style={{ paddingTop: "16px" }}>
          <span
            style={{
              fontSize: "12px",
              lineHeight: "20px",
              letterSpacing: "0.4px",
              fontWeight: 300,
            }}
          >
            {t("supportDocument")}
          </span>
          <Button
            component="label"
            variant="outlined"
            fullWidth
            startIcon={<AttachFileIcon />}
            data-testid="waiver-upload-button"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            style={{
              background: isDragActive ? "#EAEFFF" : "#F8F9FD",
              borderColor: isDragActive ? "#3E5EEC" : undefined,
              borderStyle: "dashed",
            }}
          >
            <span>{t("dragAndDropOrUpload")}</span>
            <VisuallyHiddenInput
              type="file"
              accept={SUPPORTED_DOCUMENT_ACCEPT}
              onChange={handleFileUpload}
              multiple
            />
          </Button>
        </Grid>

        {fileError ? (
          <Grid item xs={12} style={{ paddingTop: "8px" }}>
            <div style={{ color: "#ef4444", fontSize: "12px" }}>{fileError}</div>
          </Grid>
        ) : null}

        {pendingFiles.length > 0 ? (
          <Grid item xs={12} style={{ paddingTop: "16px" }}>
            <span>
              {t("textUploaded", { count: pendingFiles.length })}
            </span>
            {pendingFiles.map((file, index) => (
              <div
                key={`${file.name}-${index}`}
                data-testid="uploaded-document-item"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "6px 10px",
                  marginBottom: "6px",
                  borderRadius: "4px",
                  background: "white",
                  border: "1px solid #B8B8C7",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", minWidth: 0, flex: 1 }}>
                  <img
                    src={FileOutlined}
                    alt="file-icon"
                    style={{ width: "16px", height: "16px", marginRight: "8px", flexShrink: 0 }}
                  />
                  <span
                    title={file.name}
                    style={{
                      fontSize: "14px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {file.name}
                  </span>
                </div>
                <IconButton
                  size="small"
                  aria-label="delete"
                  onClick={() => handleDeleteDocument(index)}
                  data-testid="delete-document-button"
                >
                  <img src={TrashOutlined} alt="delete-icon" style={{ width: "16px", height: "16px" }} />
                </IconButton>
              </div>
            ))}
          </Grid>
        ) : null}

        {submitError ? (
          <Grid item xs={12} style={{ paddingTop: "8px" }}>
            <div style={{ color: "#ef4444", fontSize: "12px" }}>
              {submitError}
            </div>
          </Grid>
        ) : null}

        <Grid
          item
          xs={12}
          style={{ display: "flex", justifyContent: "flex-end", gap: "8px", paddingTop: "16px" }}
        >
          <Button
            variant="text"
            onClick={onCancel}
            disabled={isSubmitting}
            data-testid="waiver-cancel-button"
          >
            {t("cancel")}
          </Button>
          <LoadingButton
            variant="contained"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={
              mitigationFactor.length < MIN_MITIGATION_FACTORS ||
              (isOtherSelected && !description.trim()) ||
              Boolean(loanDetails?.tcLoanId)
            }
            data-testid="waiver-submit-button"
          >
            {t("submitWaiver")}
          </LoadingButton>
        </Grid>
      </Grid>
    </Box>
  );
}
